<template>
  <div
    ref="comboboxRoot"
    class="p-input-combobox w-full"
    :class="disabled || preventInput ? 'pointer-events-none select-none' : ''"
  >
    <label v-if="label" :id="ownLabelId" :for="id" class="block text-sm font-medium">{{
      label
    }}</label>
    <div class="relative" :style="{ marginBottom: isOpen && grow ? `${optionsHeight}px` : '' }">
      <!-- The menu controls text; the compatibility model retains the selected domain object. -->
      <PInputMenu
        ref="inputMenu"
        v-bind="{ ...$attrs, ...inputAriaAttrs }"
        :id="id"
        name=""
        :mode="multiple ? 'combobox' : 'autocomplete'"
        :multiple="multiple"
        :by="itemsKeyName"
        :disabled="disabled"
        :model-value="multiple ? multipleValue : displayValue"
        :search-term="multiple ? query : displayValue"
        :items="[]"
        :open="isOpen"
        :open-on-focus="false"
        :open-on-click="false"
        :reset-search-term-on-blur="false"
        :reset-search-term-on-select="multiple"
        :reset-model-value-on-clear="false"
        :portal="autoPosition && !grow"
        :ignore-filter="true"
        :trailing="showButton"
        :placeholder="inputPlaceholder"
        :content="contentProps"
        :ui="{
          root: $slots['inline-input'] ? 'w-full flex-wrap gap-x-2 gap-y-1 p-1 pr-9' : 'w-full',
          base: inputClasses,
          tagsItem: selectedItemClass,
          tagsInput: inputClass,
          content: contentClass,
          viewport: 'hidden'
        }"
        :style="comboboxHeight ? { height: comboboxHeight } : {}"
        :tabindex="disabled || preventInput ? -1 : $attrs.tabindex"
        autocomplete="off"
        draggable="false"
        @dragstart.prevent
        @input.capture="onInputCapture"
        @input="onInput"
        @compositionend="onCompositionEnd"
        @click="onInputClicked"
        @keydown.capture="onKeydown"
        @keyup="onKeyup"
        @focusin="onFocusIn"
        @focusout="onFocusOut"
        @mousedown="onMousedown"
        @mouseup="rightMouseDown = false"
        @update:open="onOpenChange"
        @highlight="highlightedItem = $event?.value"
        @update:model-value="onMenuModelValueUpdate"
        @update:search-term="onMenuSearchTermUpdate"
      >
        <template #tags-item-text="{ item }">{{ getItemValue(item) }}</template>
        <template v-if="$slots['before-input']" #leading><slot name="before-input" /></template>
        <template #default>
          <slot name="inline-input" />
          <div
            v-if="!multiple && $slots['custom-selected-item']"
            ref="customItemSlotWrapper"
            class="pointer-events-none absolute inset-x-0 top-0 rounded-md py-1 pr-8 pl-2 text-sm"
            :class="[
              wrapCustomSelectedItem ? 'break-words' : 'truncate',
              disabled ? 'text-gray-400' : '',
              disabled || preventInput ? 'pointer-events-auto' : '',
              $slots['before-input'] ? 'pl-8' : ''
            ]"
          >
            <slot v-if="showCustomSelectedItem" name="custom-selected-item" :item="selectedItem" />
          </div>
          <div
            v-if="errors.length || isLoading"
            class="pointer-events-none absolute inset-y-0 right-8 flex items-center gap-2"
          >
            <PBadge v-if="isLoading" color="secondary" class="flex items-center gap-1">
              <PProgress
                as="span"
                size="sm"
                aria-label="Gegevens worden geladen"
                class="w-12! shrink-0"
              />
              <slot name="loading-text">Gegevens laden...</slot>
            </PBadge>
            <FontAwesomeIcon
              v-if="errors.length"
              :icon="['fas', 'circle-exclamation']"
              class="text-warning"
            />
          </div>
        </template>
        <template v-if="showButton" #trailing>
          <FontAwesomeIcon :icon="['fas', isOpen ? 'chevron-up' : 'chevron-down']" size="sm" />
        </template>
        <template v-if="$slots['top-of-options']" #content-top>
          <ComboboxItem :value="topOption" disabled aria-disabled="true" class="px-2 py-1"
            ><slot name="top-of-options"
          /></ComboboxItem>
        </template>
        <template #content-bottom>
          <PInputComboxOptions ref="options">
            <!-- Render compatibility slots in the menu's Reka context, including disabled ARIA state. -->
            <template v-for="(item, index) in filteredItems" :key="item[itemsKeyName]">
              <slot
                name="before-option"
                :item="item"
                :previous-item="filteredItems[Number(index) - 1]"
                :next-item="filteredItems[Number(index) + 1]"
              />
              <ComboboxItem
                :value="item"
                :disabled="isDisabled(item) || allOptionsDisabled"
                :aria-disabled="isDisabled(item) || allOptionsDisabled || undefined"
                class="input-combobox__option data-highlighted:bg-primary data-highlighted:text-on-primary cursor-pointer break-words select-none data-disabled:cursor-not-allowed data-disabled:opacity-50"
                :class="{ 'blur-xs': blurItems }"
                as-child
                @select="onItemSelect($event, item)"
              >
                <div
                  :aria-selected="isSelected(item)"
                  :data-combobox-item-key="String(item[itemsKeyName])"
                >
                  <slot
                    name="before-list-item"
                    :item="item"
                    :previous-item="filteredItems[Number(index) - 1]"
                    :next-item="filteredItems[Number(index) + 1]"
                  />
                  <slot
                    v-if="$slots['custom-list-item']"
                    name="custom-list-item"
                    :item="item"
                    :active="highlightedItem === item"
                    :disabled="isDisabled(item) || allOptionsDisabled"
                  />
                  <span
                    v-else
                    class="block py-2 pl-2"
                    :class="{ 'font-semibold': isSelected(item) }"
                    >{{
                      getItemValue(item, {
                        active: highlightedItem === item,
                        selected: isSelected(item)
                      })
                    }}</span
                  >
                </div>
              </ComboboxItem>
              <slot
                name="after-list-item"
                :item="item"
                :next-item="filteredItems[Number(index) + 1]"
                :visible-items="filteredItems"
                :disabled="allOptionsDisabled"
              />
            </template>
            <slot
              name="custom-options"
              :disabled="allOptionsDisabled"
              :on-mouse-down-left="optionMouseDown"
              :on-mouse-up-left="optionMouseUp"
            />
            <ComboboxItem
              v-if="creationOption"
              :value="createOption"
              :disabled="allOptionsDisabled"
              :aria-disabled="allOptionsDisabled || undefined"
              class="data-highlighted:bg-primary data-highlighted:text-on-primary cursor-pointer px-2 py-2"
              @select="onCreateSelect"
            >
              <FontAwesomeIcon :icon="['fas', 'plus']" size="xs" class="mr-1" />{{
                newItemText || "Nieuw item"
              }}
            </ComboboxItem>
            <div
              v-if="scrollPadding > 0"
              role="presentation"
              aria-hidden="true"
              class="pointer-events-none"
              :style="{ height: `${scrollPadding}px` }"
            />
          </PInputComboxOptions>
        </template>
        <template #empty><span /></template>
      </PInputMenu>
    </div>
    <p v-if="showOwnError" :id="legacyErrorId" class="text-error mt-2 text-sm">{{ errors[0] }}</p>
    <input
      v-for="field in formFields"
      :key="field.name"
      type="hidden"
      :name="field.name"
      :value="field.value"
      :disabled="disabled"
    />
    <slot />
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  provide,
  ref,
  toRaw,
  useAttrs,
  useId,
  useSlots,
  useTemplateRef,
  watch
} from "vue"
import { useEventListener } from "@vueuse/core"
import { ComboboxItem } from "reka-ui"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import { formFieldInjectionKey, useFormField } from "@nuxt/ui/composables/useFormField"
import PInputMenu from "./PInputMenu.vue"
import PInputComboxOptions, { inputComboxActionKey } from "./PInputComboxOptions.vue"
import PProgress from "@/components/elements/PProgress.vue"
import PBadge from "@/components/elements/PBadge.vue"
import { useModelFormStore } from "@/store/model-forms.store"
import type { BaseModel } from "@/models/base.model"
import type { ModelConstructorSignature } from "@/models/types/model-constructor-signature"
import { ModelFactory } from "@/factories/model-factory"
import { CustomComboboxOptionWrapper } from "@/components/form/custom-combobox-option-wrapper"

const props = withDefaults(
  defineProps<{
    id?: string
    name?: string
    disabled?: boolean
    /** The current value of the combobox, can be string, object, number, or null. */
    modelValue?: string | object | number | null | undefined
    /** Use InputMenu's native array selection and removable tags. */
    multiple?: boolean
    /** Optional classes for the native selected tags. */
    selectedItemClass?: string
    /** The array of items to display in the combobox options. */
    items: any[]
    /** Items have already been filtered, ordered and limited by the caller. */
    itemsAlreadyFiltered?: boolean
    /** The key name in each item object to use as the unique identifier. */
    itemsKeyName: string
    /** The key name to display for each item, if not using a custom function. */
    itemsValueName?: string
    /** Function to determine the display value of an item, given its status options. */
    itemsValueFunction?: (
      item: any,
      options: { active?: boolean; selected?: boolean; disabled?: boolean }
    ) => string
    /** Text to display in the combobox on mount. */
    textOnMounted?: string | null
    /** If true, returns a string instead of null when no matches are found. */
    returnStringIfNoMatches?: boolean
    /** If true, returns null if no matches are found. */
    returnNullIfNoMatches?: boolean
    /** If true, returns null when focus is lost with no matches. */
    returnNullOnFocusOutAndNoMatches?: boolean
    /** Determines whether to show the dropdown button next to the input. */
    showButton?: boolean
    /** Label text for the combobox. */
    label?: string
    /** Custom CSS class for the combobox input element. */
    inputClass?: string
    /** Placeholder text for the combobox input. */
    inputPlaceholder?: string
    /** Minimum number of characters required in the query to show options. */
    minCharactersRequired?: number
    /** Enables thorough search through item values. */
    searchThoroughly?: boolean
    /** Allows creation of a new item option if no matches are found. */
    creationOption?: boolean
    /** Type of model to use for creating a new item. */
    creationModelType?: ModelConstructorSignature<any, any>
    /** Parameters to use when creating a new item. */
    creationParameters?: Record<string, unknown>
    /** If true, automatically selects newly created item after creation. */
    autoSelectNewlyCreatedModel?: boolean
    /** Indicates if the combobox input is editable. */
    editable?: boolean
    /** Custom path for editing object in model. */
    customEditObjPath?: string | null
    /** Function to convert a selected model to a base model when editing. */
    editModelConversionFunction?: (selectedModel: any) => BaseModel | undefined
    /** Maximum number of items to display in the combobox options. */
    maxItemCount?: number
    /** Sorts the options alphabetically if set to true. */
    sortAlphabetically?: boolean
    /** Function to display a custom selected item. */
    selectedItemDisplayFn?: (item: any) => string | undefined
    /** Custom sorting function for items. */
    itemsSortFn?: (a: any, b: any) => number
    /** Custom filtering function for items. */
    itemsFilterFn?: (item: any, query: string) => boolean
    /** Custom include criterium function for items. */
    includeCriteriumFn?: (item: any, query: string) => boolean
    /** Prevents input from being editable if set to true. */
    preventInput?: boolean
    /** Array of error messages to display below the combobox. */
    errors?: string[]
    /** Wraps custom selected item display in text wrapping mode. */
    wrapCustomSelectedItem?: boolean
    /** Automatically focuses on the combobox input on load if set to true. */
    autoFocus?: boolean
    /** Auto-selects an item if it matches the query exactly. */
    autoSelectOnExactMatch?: boolean
    /** Shows the options dropdown on focus if true. */
    showOptionsOnFocus?: boolean
    /** Shows the options dropdown on click if true. */
    showOptionsOnClick?: boolean
    /** Array of items to disable in the options. */
    disabledItems?: any[]
    /** If true, inactivates the active option when the query is empty. */
    inactivateActiveOptionOnEmptyQuery?: boolean
    /** Expands combobox height to fit options if set to true. */
    grow?: boolean
    /** Opens options on the side with enough viewport space. Ignored when grow is enabled. */
    autoPosition?: boolean
    /** If true, shows a loading spinner */
    isLoading?: boolean
    /** Hook into the start of the onModelValueUpdate function. Allows canceling item selection altogether. */
    beforeModelValueUpdateHandler?: (val: any) => boolean
    /** Blur filtered items (e.g. for showing items being loaded state) */
    blurItems?: boolean
    newItemText?: string
  }>(),
  {
    disabled: undefined,
    modelValue: null,
    returnStringIfNoMatches: false,
    returnNullIfNoMatches: true,
    returnNullOnFocusOutAndNoMatches: false,
    showButton: true,
    minCharactersRequired: 0,
    searchThoroughly: false,
    creationOption: false,
    creationParameters: () => ({}),
    autoSelectNewlyCreatedModel: true,
    editable: true,
    customEditObjPath: null,
    maxItemCount: 50,
    sortAlphabetically: true,
    preventInput: false,
    errors: () => [],
    wrapCustomSelectedItem: true,
    autoFocus: false,
    autoSelectOnExactMatch: false,
    showOptionsOnFocus: false,
    showOptionsOnClick: false,
    inactivateActiveOptionOnEmptyQuery: true,
    grow: false,
    isLoading: false,
    beforeModelValueUpdateHandler: () => true,
    blurItems: false
  }
)

const emit = defineEmits([
  "update:modelValue",
  "itemCreated",
  "itemUpdated",
  "itemSelected",
  "isCreatingNewItem",
  "itemDeselected",
  "queryChanged",
  "comboboxOptionsOpenChange",
  "scrollend"
])

defineOptions({ inheritAttrs: false })
const slots = useSlots()
const attrs = useAttrs()
const formField = inject(formFieldInjectionKey, undefined)
const {
  id,
  disabled,
  labelId,
  ariaAttrs,
  emitFormFocus,
  emitFormBlur,
  emitFormInput,
  emitFormChange
} = useFormField(props)
const ownLabelId = `${useId()}-label`
const legacyErrorId = `${useId()}-error`
const showOwnError = computed(() => props.errors.length > 0 && !formField?.value?.error)
const inputAriaAttrs = computed(() => ({
  ...ariaAttrs.value,
  "aria-invalid":
    props.errors.length > 0 || (attrs["aria-invalid"] ?? ariaAttrs.value?.["aria-invalid"]),
  "aria-labelledby":
    attrs["aria-labelledby"] ?? labelId.value ?? (props.label ? ownLabelId : undefined),
  "aria-describedby":
    [
      ariaAttrs.value?.["aria-describedby"],
      attrs["aria-describedby"],
      showOwnError.value ? legacyErrorId : undefined
    ]
      .filter(Boolean)
      .join(" ") || undefined
}))
const showCustomSelectedItem = computed(
  () =>
    !props.multiple && !!slots["custom-selected-item"] && !!selectedItem.value && !hasFocus.value
)
const inputClasses = computed(() =>
  [
    props.multiple ? "" : "px-2 py-1 text-base leading-none sm:text-sm",
    props.inputClass,
    slots["inline-input"] ? "order-last min-w-20 w-0 flex-1 pe-2!" : "",
    showCustomSelectedItem.value
      ? "text-transparent! disabled:text-transparent! transition-none"
      : ""
  ]
    .filter(Boolean)
    .join(" ")
)
const contentClass = computed(() =>
  [
    "overflow-y-auto py-1 max-h-[min(16rem,var(--reka-combobox-content-available-height,16rem))]",
    props.autoPosition && !props.grow
      ? "z-50 w-(--reka-combobox-trigger-width)"
      : "absolute top-full left-0 mt-1 w-full z-40"
  ].join(" ")
)
const inputMenu = useTemplateRef("inputMenu")
const comboboxRoot = useTemplateRef<HTMLDivElement>("comboboxRoot")
const options = useTemplateRef("options")
const customItemSlotWrapper = useTemplateRef("customItemSlotWrapper")
const nativeInput = computed(() => inputMenu.value?.inputRef)
const query = ref(props.textOnMounted ?? "")
const selectedItem = ref<any>(null)
const isOpen = ref(false)
const hasFocus = ref(false)
const rightMouseDown = ref(false)
const optionLmbDown = ref(false)
const anyKeyDown = ref(false)
const allOptionsDisabled = ref(props.inactivateActiveOptionOnEmptyQuery && query.value.length === 0)
const highlightedItem = ref<any>()
const topOption = { type: "heading" }
const createOption = { type: "create" }
const optionsHeight = ref(0)
const customHeight = ref<number | null>(null)
const comboboxHeight = computed(() => (customHeight.value ? `${customHeight.value}px` : undefined))
const modelFormStore = useModelFormStore()
let doubleClickTimer: ReturnType<typeof setTimeout> | undefined
let blurTimer: ReturnType<typeof setTimeout> | undefined
let nextClickIsDoubleClick = false
let resizeObserver: ResizeObserver | undefined
let unmounted = false
let clickedOutside = false
let compositionCommitPending = false
const contentProps = computed(() => ({
  position: props.autoPosition && !props.grow ? ("popper" as const) : ("inline" as const),
  sideOffset: 4,
  onMouseenter: () => {
    allOptionsDisabled.value = false
  },
  onMousedown: onOptionsMousedown,
  onMouseup: optionMouseUp,
  onScrollend: () => emit("scrollend")
}))
function onOptionsMousedown(event: MouseEvent) {
  if (event.button !== 0) return
  optionMouseDown()
  // Keep input focus until selection is committed, so focusout autosaves the selected value.
  if (
    !props.multiple &&
    event.target instanceof Element &&
    event.target.closest('[role="option"]')
  ) {
    event.preventDefault()
  }
}
function optionMouseDown() {
  optionLmbDown.value = true
}
function optionMouseUp() {
  optionLmbDown.value = false
}
// Convert disabled items array to dictionary for constant time lookup.
const disabledItemsDict = computed(() => {
  const dict: any = {}
  if (props.disabledItems) {
    for (const item of props.disabledItems) {
      dict[item[props.itemsKeyName]] = true
    }
  }
  return dict
})

const filteredItems: any = computed(() => {
  if (!isOpen.value) {
    return [] // Increase performance by not filtering when the combobox results are not visible.
  }

  if (query.value.length < props.minCharactersRequired) {
    if (selectedItem.value) {
      return [selectedItem.value]
    }
    return []
  }

  if (props.itemsAlreadyFiltered)
    return props.multiple ? props.items.filter((item) => !isSelected(item)) : props.items

  const filtered = []

  for (let i = 0, count = 0; i < props.items.length && count < props.maxItemCount; i++) {
    const item = props.items[i]
    if (props.multiple && isSelected(item)) continue
    let includeCriteriumIsMet

    if (props.searchThoroughly) {
      const superString = getItemValue(item).toLowerCase()
      const substrings = query.value.toString().toLowerCase().split(" ")
      includeCriteriumIsMet = substrings.every((substring) => superString.includes(substring))
    } else if (props.itemsFilterFn) {
      includeCriteriumIsMet = props.itemsFilterFn(item, query.value)
    } else if (props.includeCriteriumFn) {
      includeCriteriumIsMet = props.includeCriteriumFn(item, query.value)
    } else {
      includeCriteriumIsMet = getItemValue(item)
        .toLowerCase()
        .includes(query.value.toString().toLowerCase())
    }

    if (includeCriteriumIsMet) {
      filtered.push(item)
      count++
    }
  }

  let sorted = filtered
  if (props.sortAlphabetically) {
    sorted = filtered.sort((a: any, b: any) => {
      const aValue = getItemValue(a).toLowerCase()
      const bValue = getItemValue(b).toString().toLowerCase()

      if (aValue < bValue) return -1
      if (aValue > bValue) return 1
      return 0
    })
  }

  if (props.itemsSortFn) {
    return sorted.sort(props.itemsSortFn)
  }

  return sorted
})

const displayValue = computed(() => {
  if (!selectedItem.value) {
    return query.value
  }
  if (props.selectedItemDisplayFn) {
    return props.selectedItemDisplayFn(selectedItem.value) ?? ""
  }
  return getItemValue(selectedItem.value)
})
const multipleValue = computed<any[]>(() =>
  Array.isArray(props.modelValue) ? props.modelValue : []
)

// Preserve object-valued HTML form submission independently of the visible text.
const formFields = computed(() => {
  const fields: { name: string; value: string }[] = []
  function append(key: string, value: unknown) {
    if (value == null) fields.push({ name: key, value: "" })
    else if (value instanceof Date) fields.push({ name: key, value: value.toISOString() })
    else if (typeof value === "object")
      Object.entries(value).forEach(([part, entry]) => append(`${key}[${part}]`, entry))
    else
      fields.push({
        name: key,
        value: typeof value === "boolean" ? (value ? "1" : "0") : String(value)
      })
  }
  // A validation field name must not serialize the selected model's relations.
  if (props.name && (props.multiple || selectedItem.value))
    append(props.name, props.multiple ? multipleValue.value : selectedItem.value)
  return fields
})
function onItemSelect(event: Event, item: any) {
  if (props.multiple) {
    // Let InputMenu/Reka toggle its native multiple model and retain input focus.
    if (isDisabled(item) || allOptionsDisabled.value || !props.beforeModelValueUpdateHandler(item))
      event.preventDefault()
    return
  }
  event.preventDefault()
  if (isDisabled(item) || allOptionsDisabled.value) return
  const version = openRequestVersion
  if (props.beforeModelValueUpdateHandler(item)) selectItem(item)
  if (version === openRequestVersion) isOpen.value = false
}
function onMenuModelValueUpdate(value: unknown) {
  // Keep Reka text updates separate from the selected domain object.
  if (!props.multiple && typeof value === "string") {
    onTextInput(value)
    return
  }
  if (!props.multiple || !Array.isArray(value)) return
  const previous = multipleValue.value
  emitModelValue(value)
  for (const item of value) {
    if (!previous.some((selected) => selected[props.itemsKeyName] === item[props.itemsKeyName]))
      emit("itemSelected", item)
  }
  for (const item of previous) {
    if (!value.some((selected) => selected[props.itemsKeyName] === item[props.itemsKeyName]))
      emit("itemDeselected", item)
  }
}
function onMenuSearchTermUpdate(value: string) {
  if (props.multiple && value !== query.value) setQuery(value, true)
}
function emitModelValue(value: string | object | number | null) {
  const echo = { value }
  pendingModelEcho = echo
  emit("update:modelValue", value)
  emitFormInput()
  emitFormChange()
  nextTick(() => {
    if (pendingModelEcho === echo) pendingModelEcho = undefined
  })
}
let pendingModelEcho: { value: unknown } | undefined
function setQuery(value: string, notify = false) {
  query.value = value
  if (notify) emit("queryChanged", value)
}
function onInputCapture(event: Event) {
  // A final keyboard input can arrive after focusout; Reka would reopen and focus the menu.
  if (!props.multiple && !hasFocus.value && event.target === nativeInput.value)
    event.stopImmediatePropagation()
}
function onInput(event: Event) {
  if (disabled.value || props.preventInput || event.target !== nativeInput.value) return
  if (props.multiple) {
    if ((event as InputEvent).isComposing) return
    onMenuSearchTermUpdate((event.target as HTMLInputElement).value)
    isOpen.value = true
  } else if ((event as InputEvent).isComposing) {
    // The original combobox filters and emits the current text during composition too.
    onTextInput((event.target as HTMLInputElement).value)
  }
}
function onCompositionEnd(event: CompositionEvent) {
  // Reka defers this commit to nextTick. Commit now, before a focusout can discard it.
  if (!props.multiple && event.target === nativeInput.value) {
    compositionCommitPending = true
    onTextInput((event.target as HTMLInputElement).value)
    nextTick(() => {
      compositionCommitPending = false
    })
  }
}
function onTextInput(value: string) {
  if (!hasFocus.value || disabled.value || props.preventInput) return
  if (value === query.value && (!selectedItem.value || value === getItemValue(selectedItem.value)))
    return
  const wasSelected = !!selectedItem.value
  clickedOutside = false
  selectedItem.value = null
  query.value = value
  isOpen.value = true
  const exact =
    props.autoSelectOnExactMatch &&
    filteredItems.value.length === 1 &&
    getItemValue(filteredItems.value[0]).toLowerCase() === value.toLowerCase()
  if (wasSelected) {
    if (!props.returnStringIfNoMatches && props.returnNullIfNoMatches) emitModelValue(null)
    emit("itemDeselected")
  }
  if (exact) handleSelection(filteredItems.value[0], true)
  else if (props.returnStringIfNoMatches) emitModelValue(value)
  if (!exact) emit("queryChanged", value)
}
function onOpenChange(value: boolean) {
  if (disabled.value || props.preventInput) return
  if (value && !isOpen.value && !props.multiple) setQuery("")
  isOpen.value = value
}
function onInputClicked(event: MouseEvent) {
  if (disabled.value || props.preventInput || event.target !== nativeInput.value) return
  nativeInput.value?.select()
  if (nextClickIsDoubleClick && selectedItem.value) editSelectedItem()
  nextClickIsDoubleClick = true
  clearTimeout(doubleClickTimer)
  doubleClickTimer = setTimeout(() => {
    nextClickIsDoubleClick = false
  }, 200)
  if (props.showOptionsOnClick) {
    if (!props.multiple) onTextInput(nativeInput.value?.value ?? "")
    isOpen.value = true
  }
}
function onMousedown(event: MouseEvent) {
  rightMouseDown.value = event.button === 2
}
function onFocusIn() {
  if (rightMouseDown.value) return
  hasFocus.value = true
  clickedOutside = false
  emitFormFocus()
  if (props.showOptionsOnFocus) {
    if (!props.multiple) onTextInput(nativeInput.value?.value ?? "")
    isOpen.value = true
  }
}
function onFocusOut() {
  // Headless UI commits the active keyboard result on blur, including an exact match.
  if (
    !props.multiple &&
    isOpen.value &&
    query.value &&
    !optionLmbDown.value &&
    !clickedOutside &&
    !allOptionsDisabled.value
  ) {
    const item = highlightedItem.value ?? filteredItems.value[0]
    // Menu actions such as "Nieuw item" are not selectable domain models.
    if (toRaw(item) === createOption) {
      if (props.beforeModelValueUpdateHandler(false)) initiateNewItemCreation()
    } else if (item && !isDisabled(item) && props.beforeModelValueUpdateHandler(item))
      handleSelection(item, true)
  }
  if (!props.multiple && !optionLmbDown.value) {
    // Let Reka finish its queued compositionend before closing; otherwise it reopens and focuses.
    if (compositionCommitPending)
      nextTick(() => {
        isOpen.value = false
      })
    else isOpen.value = false
  }
  if (!optionLmbDown.value && !anyKeyDown.value) {
    if (!selectedItem.value && !props.returnStringIfNoMatches) query.value = ""
    if (props.returnNullOnFocusOutAndNoMatches && !selectedItem.value) emitModelValue(null)
  }
  hasFocus.value = false
  anyKeyDown.value = false
  emitFormBlur()
}
function onKeydown(event: KeyboardEvent) {
  anyKeyDown.value = true
  clickedOutside = false
  if (event.ctrlKey && event.key === "Enter") {
    event.preventDefault()
    event.stopImmediatePropagation()
    editSelectedItem()
  } else if (event.key === "Escape") {
    isOpen.value = false
    blur()
  } else if (
    event.key === "Tab" ||
    (event.key === "Enter" && !event.metaKey && !event.altKey && !event.isComposing)
  ) {
    const version = openRequestVersion
    if (query.value && isOpen.value) {
      const activeId = nativeInput.value?.getAttribute("aria-activedescendant")
      // A fast key press can precede Reka's initial highlight. Use the first enabled
      // option so the same selection veto also handles pending consumer searches.
      const active = activeId
        ? document.getElementById(activeId)
        : getListbox()?.querySelector<HTMLElement>('[role="option"]:not([data-disabled])')
      if (event.key === "Tab" || !activeId) {
        active?.click()
        if (event.key === "Enter" && active) {
          event.preventDefault()
          event.stopImmediatePropagation()
        }
      }
    }
    if (event.key === "Tab" && version === openRequestVersion) isOpen.value = false
  }
}
function onKeyup() {
  anyKeyDown.value = false
}
function onCreateSelect(event: Event) {
  event.preventDefault()
  if (allOptionsDisabled.value) return
  const version = openRequestVersion
  if (props.beforeModelValueUpdateHandler(false)) initiateNewItemCreation()
  if (version === openRequestVersion) isOpen.value = false
}
function handleSelection(value: any, preventBlur = false) {
  if (props.multiple) {
    if (!value || isSelected(value)) return
    isOpen.value = true
    nextTick(() => options.value?.selectItem(value))
    return
  }
  if (value instanceof CustomComboboxOptionWrapper) {
    selectedItem.value = null
    value.onSelect(value.item)
    return
  }
  if (props.creationOption && value === false) {
    initiateNewItemCreation()
    return
  }
  if (!value) return
  selectedItem.value = value
  setQuery(getItemValue(value))
  emitModelValue(value)
  emit("itemSelected", value)
  if (!preventBlur) {
    clearTimeout(blurTimer)
    blurTimer = setTimeout(blur)
  }
}
function selectItem(item: any) {
  handleSelection(item)
}
// Cancelling an auxiliary form restores the display without another user selection.
function restoreSelection(item: any) {
  selectedItem.value = item
  setQuery(item ? getItemValue(item) : "")
}
function focus() {
  clearTimeout(blurTimer)
  nextTick(() => {
    if (!unmounted) nativeInput.value?.focus()
  })
}
function blur() {
  nativeInput.value?.blur()
}
function select() {
  clearTimeout(blurTimer)
  nextTick(() => {
    if (!unmounted) {
      nativeInput.value?.focus()
      nativeInput.value?.select()
    }
  })
}
function setForceOpenState(state: boolean) {
  openRequestVersion++
  isOpen.value = state
}
let requestedHighlightKey: unknown
let openRequestVersion = 0
const scrollPadding = ref(0)
let alignmentFrame: number | undefined
let alignmentVersion = 0
function resetScrollAlignment() {
  alignmentVersion++
  if (alignmentFrame !== undefined) cancelAnimationFrame(alignmentFrame)
  alignmentFrame = undefined
  scrollPadding.value = 0
}
function getListbox() {
  return inputMenu.value?.viewportRef?.closest<HTMLElement>('[role="listbox"]') ?? null
}
function alignOptionToTop(target: HTMLElement) {
  resetScrollAlignment()
  const version = alignmentVersion
  // Let Reka finish its opening/highlight work before positioning the expanded group.
  function afterHighlight(frames: number) {
    alignmentFrame = requestAnimationFrame(async () => {
      if (version !== alignmentVersion || !isOpen.value || unmounted) return
      if (frames > 1) {
        afterHighlight(frames - 1)
        return
      }
      alignmentFrame = undefined
      const listbox = getListbox()
      if (!listbox || !target.isConnected) return
      const heading = target.previousElementSibling as HTMLElement | null
      const headingHeight = heading?.hasAttribute("data-combobox-group-heading")
        ? heading.getBoundingClientRect().height
        : 0
      const top = Math.max(
        0,
        listbox.scrollTop +
          target.getBoundingClientRect().top -
          headingHeight -
          listbox.getBoundingClientRect().top -
          listbox.clientTop -
          (parseFloat(getComputedStyle(listbox).paddingTop) || 0) -
          12
      )
      scrollPadding.value = Math.max(
        0,
        Math.ceil(top + listbox.clientHeight - listbox.scrollHeight)
      )
      await nextTick()
      if (version === alignmentVersion && isOpen.value && listbox.isConnected)
        listbox.scrollTop = top
      if (version === alignmentVersion) requestedHighlightKey = undefined
    })
  }
  afterHighlight(3)
}
function open(itemToHighlight?: any) {
  if (disabled.value || props.preventInput || unmounted) return
  openRequestVersion++
  requestedHighlightKey = itemToHighlight?.[props.itemsKeyName]
  isOpen.value = true
  focus()
  highlightFirstOption()
}
function highlightFirstOption() {
  nextTick(() => {
    if (!isOpen.value || unmounted) return
    if (requestedHighlightKey !== undefined) {
      const target = Array.from(
        getListbox()?.querySelectorAll<HTMLElement>("[data-combobox-item-key]") ?? []
      ).find((option) => option.dataset.comboboxItemKey === String(requestedHighlightKey))
      if (target) {
        options.value?.highlightItem(target)
        alignOptionToTop(target)
      }
    } else options.value?.highlightFirstItem()
  })
}
provide(inputComboxActionKey, (action) => {
  const version = openRequestVersion
  if (!props.beforeModelValueUpdateHandler(action)) return
  action.onSelect(action.item)
  // Group expansion requests open(item) on the next tick; keep its list and row nodes mounted.
  nextTick(() => {
    if (version === openRequestVersion && !unmounted) isOpen.value = false
  })
})
function editSelectedItem() {
  if (!props.editable || !selectedItem.value) return
  const model = props.editModelConversionFunction
    ? props.editModelConversionFunction(selectedItem.value)
    : selectedItem.value
  if (!model) return
  query.value = getItemValue(selectedItem.value)
  modelFormStore.openForm({
    model,
    saveHandle: onItemSaved,
    closeHandle: onItemFormClose,
    creationParameters: props.creationParameters
  })
}
function initiateNewItemCreation() {
  emit("isCreatingNewItem", true)
  if (!props.creationModelType)
    throw new Error("If creationOption is true, creationModelType must be set.")
  modelFormStore.openForm({
    model: ModelFactory.createModel(props.creationModelType),
    modelType: props.creationModelType,
    saveHandle: onItemSaved,
    closeHandle: onItemFormClose,
    textInput: query.value,
    creationParameters: props.creationParameters
  })
  selectedItem.value = null
}
function onItemSaved(item: BaseModel, isCreating = true) {
  if (props.autoSelectNewlyCreatedModel) nextTick(() => handleSelection(item))
  emit(isCreating ? "itemCreated" : "itemUpdated", item)
  focus()
}
function onItemFormClose() {
  emit("isCreatingNewItem", false)
}
function getItemValue(item: any, options?: any) {
  if (props.itemsValueName) {
    if (props.itemsValueName.includes(".")) {
      /* Support for recursively getting to nested objects (using dot notation) */
      const fields = props.itemsValueName.split(".")
      for (const field of fields) {
        item = item[field]
      }
      return item?.toString() ?? ""
    }
    // Access the property value (works for both regular properties and getters)
    const value = item[props.itemsValueName]
    return value?.toString() ?? ""
  } else if (props.itemsValueFunction) {
    // Function based display text. Pass item to function, along with options object.
    return props.itemsValueFunction(item, {
      active: options?.active,
      selected: options?.selected,
      disabled: isDisabled(item)
    })
  }
}

function isSelected(item: any): boolean {
  if (props.multiple)
    return multipleValue.value.some(
      (selected) => selected[props.itemsKeyName] === item[props.itemsKeyName]
    )
  if (!selectedItem.value) return false
  return (
    selectedItem.value === item ||
    (item[props.itemsKeyName] != null &&
      item[props.itemsKeyName] === selectedItem.value[props.itemsKeyName])
  )
}
function isDisabled(item: any): boolean {
  return !!disabledItemsDict.value[item[props.itemsKeyName]]
}
function syncModel(initial = false) {
  if (props.multiple) return
  const value = props.modelValue
  if (initial) {
    selectedItem.value = value || null
  } else {
    if (!value && value !== selectedItem.value) query.value = ""
    if (typeof value === "object" && value !== selectedItem.value) selectedItem.value = value
  }
}
syncModel(true)
watch(
  () => props.modelValue,
  (value) => {
    if (pendingModelEcho && value === pendingModelEcho.value) {
      pendingModelEcho = undefined
      return
    }
    syncModel()
  }
)
watch(
  () => props.items,
  () => {
    if (props.multiple) return
    const matches = props.items.filter((item) => item[props.itemsKeyName] === props.modelValue)
    if (matches.length === 1) selectedItem.value = matches[0]
  }
)
watch(
  () => props.disabledItems,
  () => options.value?.highlightFirstItem(),
  { deep: true, flush: "post" }
)
watch(
  () => query.value.length,
  (length) => {
    allOptionsDisabled.value = props.inactivateActiveOptionOnEmptyQuery && length === 0
  },
  { immediate: true }
)
watch(
  isOpen,
  (value) => {
    if (!value) {
      requestedHighlightKey = undefined
      resetScrollAlignment()
    }
    emit("comboboxOptionsOpenChange", value)
  },
  { immediate: true }
)
watch(query, () => {
  requestedHighlightKey = undefined
  resetScrollAlignment()
})
watch(() => filteredItems.value.length, highlightFirstOption)
watch([isOpen, filteredItems], async () => {
  if (!isOpen.value) return
  await nextTick()
  const listbox = inputMenu.value?.viewportRef?.closest('[role="listbox"]') as HTMLElement | null
  if (!listbox) return
  optionsHeight.value = listbox.offsetHeight + 4
  if (!props.autoPosition || props.grow)
    listbox.scrollIntoView({ block: "nearest", behavior: "instant" })
})
watch(customItemSlotWrapper, (element) => {
  resizeObserver?.disconnect()
  if (!props.wrapCustomSelectedItem || !element) return
  resizeObserver = new ResizeObserver((entries) => {
    const height = entries[0]?.contentRect.height ?? 0
    // Focusing hides the selected display; retain its height while editing to avoid a layout jump.
    if (height > 28) customHeight.value = height + 8
    else if (!hasFocus.value) customHeight.value = null
  })
  resizeObserver.observe(element)
})
useEventListener("resize", () => {
  if (props.wrapCustomSelectedItem && customItemSlotWrapper.value) {
    const height = customItemSlotWrapper.value.offsetHeight
    if (height > 28) customHeight.value = height + 8
    else if (!hasFocus.value) customHeight.value = null
  }
})
useEventListener("mouseup", optionMouseUp)
useEventListener("mousedown", (event) => {
  const target = event.target as Node | null
  clickedOutside =
    hasFocus.value &&
    !!target &&
    !comboboxRoot.value?.contains(target) &&
    !getListbox()?.contains(target)
})
watch(
  () => [props.itemsValueName, props.itemsValueFunction],
  ([nameValue, fnValue]) => {
    if (!nameValue && !fnValue)
      throw new Error("Either itemsValueName or itemsValueFunction must be set.")
    if (nameValue && fnValue)
      throw new Error("Only one of itemsValueName or itemsValueFunction can be set.")
  },
  { immediate: true }
)
onMounted(() => {
  if (props.creationOption && !props.creationModelType)
    throw new Error("If creationOption is true, creationModelType must be set.")
  if (props.autoFocus) focus()
})
onBeforeUnmount(() => {
  unmounted = true
  resetScrollAlignment()
  resizeObserver?.disconnect()
  clearTimeout(doubleClickTimer)
  clearTimeout(blurTimer)
})
defineExpose({
  focus,
  blur,
  select,
  open,
  query,
  selectItem,
  restoreSelection,
  setForceOpenState,
  highlightFirstOption
})
</script>
